// RFC 9457 problems as a typed ApiProblem error: parsed from problem+json responses, network failures and invalid responses, with UI copy per status (a used-up Assistant budget explained by its detail, never as a Retry-After countdown).
import { zProblem } from "@/api/__generated__/zod.gen";

export interface ProblemFieldError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

export interface ProblemFields {
  type: string;
  title: string;
  status: number;
  code: string;
  detail: string | null;
  instance: string | null;
  errors: ProblemFieldError[] | null;
  trace_id: string | null;
}

export class ApiProblem extends Error implements ProblemFields {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly code: string;
  readonly detail: string | null;
  readonly instance: string | null;
  readonly errors: ProblemFieldError[] | null;
  readonly trace_id: string | null;
  readonly retryAfter: number | null;

  constructor(fields: ProblemFields, options?: { retryAfter?: number | null; cause?: unknown }) {
    super(fields.detail ?? fields.title, options?.cause === undefined ? undefined : { cause: options.cause });
    this.name = "ApiProblem";
    this.type = fields.type;
    this.title = fields.title;
    this.status = fields.status;
    this.code = fields.code;
    this.detail = fields.detail;
    this.instance = fields.instance;
    this.errors = fields.errors;
    this.trace_id = fields.trace_id;
    this.retryAfter = options?.retryAfter ?? null;
  }

  get traceId(): string | null {
    return this.trace_id;
  }

  get isRetryable(): boolean {
    if (this.code === "network-error") {
      return true;
    }
    return this.status >= 500 && this.status !== 501;
  }

  static fromBody(status: number, body: unknown, headers?: Headers): ApiProblem {
    const retryAfter = parseRetryAfter(headers?.get("Retry-After") ?? null);
    const parsed = zProblem.safeParse(body);
    if (parsed.success) {
      return new ApiProblem(parsed.data, { retryAfter });
    }
    const code = status === 0 ? "network-error" : `http-${status}`;
    return new ApiProblem(
      {
        type: `urn:gridcode:problem:${code}`,
        title: statusTitle(status),
        status,
        code,
        detail: typeof body === "string" && body.length > 0 && body.length < 500 ? body : null,
        instance: null,
        errors: null,
        trace_id: null,
      },
      { retryAfter },
    );
  }

  static async fromResponse(response: Response): Promise<ApiProblem> {
    let body: unknown;
    try {
      const text = await response.text();
      body = text ? (JSON.parse(text) as unknown) : null;
    } catch {
      body = null;
    }
    return ApiProblem.fromBody(response.status, body, response.headers);
  }

  static network(cause: unknown): ApiProblem {
    return new ApiProblem(
      {
        type: "urn:gridcode:problem:network-error",
        title: "The server could not be reached",
        status: 0,
        code: "network-error",
        detail: "Check your connection and try again.",
        instance: null,
        errors: null,
        trace_id: null,
      },
      { cause },
    );
  }

  static invalidResponse(cause: unknown): ApiProblem {
    return new ApiProblem(
      {
        type: "urn:gridcode:problem:invalid-response",
        title: "The server sent an unexpected response",
        status: 0,
        code: "invalid-response",
        detail: "The response did not match the API contract.",
        instance: null,
        errors: null,
        trace_id: null,
      },
      { cause },
    );
  }
}

export function isApiProblem(value: unknown): value is ApiProblem {
  return value instanceof ApiProblem;
}

export function isAbortError(value: unknown): boolean {
  return value instanceof DOMException && value.name === "AbortError";
}

function parseRetryAfter(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.round(seconds));
  }
  const date = Date.parse(value);
  return Number.isNaN(date) ? null : Math.max(0, Math.round((date - Date.now()) / 1000));
}

function statusTitle(status: number): string {
  if (status === 0) {
    return "The server could not be reached";
  }
  if (status === 401) {
    return "Not signed in";
  }
  if (status === 403) {
    return "Access denied";
  }
  if (status === 404) {
    return "Not found";
  }
  if (status === 429) {
    return "Too many requests";
  }
  return status >= 500 ? "Server error" : "Request failed";
}

export const BUDGET_EXCEEDED = "budget-exceeded";
export const BUDGET_EXCEEDED_TITLE = "Assistant budget used up";
export const BUDGET_EXCEEDED_DESCRIPTION = "Your Assistant budget for this month is used up.";

export interface ProblemCopy {
  title: string;
  description: string;
}

export function problemCopy(problem: ApiProblem): ProblemCopy {
  switch (problem.status) {
    case 401:
      return { title: "Your session has ended", description: "Sign in again to continue." };
    case 403:
      return problem.code === "read-only-mode"
        ? { title: "Actions are turned off", description: "This server runs in read-only mode." }
        : {
            title: "You do not have access",
            description: problem.detail ?? "Your account lacks the role for this view.",
          };
    case 404:
      return {
        title: "Not found",
        description: problem.detail ?? "It may have been removed, or the link is wrong.",
      };
    case 409:
      return { title: "This conflicts with the current state", description: problem.detail ?? problem.title };
    case 429:
      if (problem.code === BUDGET_EXCEEDED) {
        return {
          title: BUDGET_EXCEEDED_TITLE,
          description: problem.detail ?? BUDGET_EXCEEDED_DESCRIPTION,
        };
      }
      return {
        title: "Too many requests",
        description:
          problem.retryAfter === null
            ? "Wait a moment, then try again."
            : `Try again in ${problem.retryAfter} s.`,
      };
    case 501:
      return {
        title: "Not available yet",
        description: "This part of the API is not implemented on this server yet.",
      };
    case 0:
      return { title: problem.title, description: problem.detail ?? "Check your connection and try again." };
    default:
      return problem.status >= 500
        ? {
            title: "Something went wrong on the server",
            description: problem.detail ?? "Try again in a moment.",
          }
        : { title: problem.title, description: problem.detail ?? "The request could not be completed." };
  }
}
